import React from 'react';
import styled from 'styled-components';
import { useParams, useHistory, useRouteMatch } from 'react-router-dom';

import { FullPageLoader } from 'shared/components/loading';
import { GqlImageBanner } from 'types/graphql';
import useErnie from 'shared/hooks/use-ernie';
import { removeTypename } from 'shared/helpers/utils';
import { AddEditImageBanner } from '../add-image-banner/add-edit-image-banner';
import { useGetMenuCustomization, useUpdateImageBanners } from '../../data-access';
import { sortByPosition } from '../image-banner-list/image-banner-list.utils';
import { normalizeBannersForUpdate } from './add-edit-image-banner-page.utils';

export function AddEditImageBannerPage(): JSX.Element {
  const { bannerId } = useParams<{ bannerId?: string }>();
  const { id: dispensaryId } = useParams<{ id: string }>();
  const history = useHistory();
  const match = useRouteMatch<{ id: string }>('/dispensaries/:id');
  const baseUrl = match?.url;
  const showErnie = useErnie();

  const { data, loading } = useGetMenuCustomization();
  const updateImageBanners = useUpdateImageBanners();

  const bannerToEdit =
    bannerId && data?.imageBanners ? data.imageBanners.find((banner) => banner._id === bannerId) ?? null : null;

  function handleClose(): void {
    history.push(`${baseUrl ?? ''}/customize/image-banner`);
  }

  async function handleSave(banner: GqlImageBanner): Promise<void> {
    if (!data?.imageBanners) {
      return;
    }

    try {
      const editImageIndex = data.imageBanners.findIndex((existingBanner) => existingBanner._id === banner._id);
      const imageBanners =
        editImageIndex > -1
          ? data.imageBanners.map((existingBanner, index) =>
              index === editImageIndex ? { ...existingBanner, ...banner } : existingBanner
            )
          : [
              ...data.imageBanners.map((existingBanner) => ({
                ...existingBanner,
                position: existingBanner.position + 1,
              })),
              banner,
            ];

      const sortedBanners = sortByPosition(imageBanners);
      const imageBannersWithPlatforms = normalizeBannersForUpdate(sortedBanners);

      await updateImageBanners({
        variables: {
          dispensaryId,
          input: {
            imageBanners: removeTypename(imageBannersWithPlatforms),
          },
        },
      });

      showErnie(bannerToEdit ? 'Image banner updated successfully!' : 'Image banner created successfully!', 'success');
      handleClose();
    } catch (error) {
      console.error('Error saving banner:', error);
      showErnie('Failed to save banner. Please try again.', 'danger');
      throw error;
    }
  }

  if (loading) {
    return (
      <ImageBannerContainer>
        <FullPageLoader />
      </ImageBannerContainer>
    );
  }

  if (!data) {
    return (
      <ImageBannerContainer>
        <div>Error loading data</div>
      </ImageBannerContainer>
    );
  }

  return (
    <ImageBannerContainer>
      <AddEditImageBanner
        bannerToEdit={bannerToEdit as GqlImageBanner | null}
        onSave={handleSave}
        onClose={handleClose}
      />
    </ImageBannerContainer>
  );
}

const ImageBannerContainer = styled.div`
  width: 100%;
`;
